import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Text, YStack } from 'tamagui'
import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import type { RequestCard } from '../../../lib/types'
import { Button } from '../../../ui'
import { useMe } from '../../users/useUsers'
import { errorText } from '../errorText'
import { TerminationForm } from '../forms/TerminationForm'
import { canEditTerminationDraft } from '../termination-types'

export function TerminationActions({ card, onDone }: { card: RequestCard; onDone: () => void }) {
  const { t } = useApp()
  const { data: me } = useMe()
  const qc = useQueryClient()
  const operationLock = useRef(false)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const [withdrawn, setWithdrawn] = useState(false)
  const withdraw = useMutation({ mutationFn: () => api.withdrawTermination(card.id), onSuccess: () => {
    setWithdrawn(true)
    qc.invalidateQueries({ queryKey: ['edo-request', card.id] })
    qc.invalidateQueries({ queryKey: ['edo-requests'] })
    onDone()
  }, onSettled: () => { operationLock.current = false; setBusy(false) } })
  const editable = canEditTerminationDraft(card, me?.id) && !!card.employment?.id && !withdrawn
  return <YStack gap={10}>
    {card.status === 'AWAITING_TERMINATION_POSTING' && <Text>{t({ ru: 'Приказ подписан, скан загружен. Проведение в 1С подтвердится по кадровой истории.', en: 'The order is signed and scanned. Posting in 1C will be confirmed from the personnel history.', az: 'Əmr imzalanıb və skan edilib. 1C-də keçirilmə kadr tarixçəsi ilə təsdiqlənəcək.' })}</Text>}
    {card.status === 'POSTED' && <Text>{t({ ru: 'Увольнение проведено в 1С.', en: 'Termination posted in 1C.', az: 'İşdən çıxma 1C-də keçirilib.' })}</Text>}
    {editable && <>
      {open ? <TerminationForm card={card} operationLock={operationLock} busy={busy} onBusyChange={setBusy} onDone={() => {}} /> : <Button disabled={busy || withdraw.isPending} onPress={() => setOpen(true)}>{t({ ru: 'Открыть черновик', en: 'Open draft' })}</Button>}
      <Text color="$muted">{t({ ru: 'Отзыв отменяет только черновик в портале.', en: 'Withdrawal cancels only the portal draft.' })}</Text>
      <Button disabled={busy || withdraw.isPending} onPress={() => { if (!operationLock.current) { operationLock.current = true; setBusy(true); withdraw.mutate() } }}>{t({ ru: 'Отозвать черновик', en: 'Withdraw draft' })}</Button>
    </>}
    {withdrawn && <Text>{t({ ru: 'Черновик отозван', en: 'Draft withdrawn' })}</Text>}
    {withdraw.error instanceof Error && <Text color="$red10">{t(errorText(withdraw.error))}</Text>}
  </YStack>
}
